import moment from "moment";
import queryString from "query-string";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { xml2js } from "../../json/xml2js";
import {
  cloneForm,
  compareForms,
  deleteFormSubmission,
  getCareTeams,
  getClientData,
  getConfig,
  getFormsySubmissionsForClient,
  getSubmission,
  handleFormChange,
  saveForm,
  sendShareEmail,
  validateForm,
} from "../../scripts/formScripts";
import {
  ArrayCheck,
  getFieldDate,
  getFieldName,
  getFieldValue,
} from "../../scripts/helperScripts";
import BackIcon from "../common/BackIcon";
import ClientFormHeader from "../common/ClientFormHeader";
import Comment from "../common/Comment";
import {
  FormInput,
  FormRadioButton,
  FormTextArea,
} from "../common/ControlBundle";
import FormButtonBar from "../common/FormButtonBar";
import ShareModal from "../common/ShareModal";
import Signature from "../common/Signature";
import { getSchema } from "./joi/validation";

function SCPPOutcome() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialProgram = location.state?.selectedProgram;

  //Get route and query string params
  const { clientid, formid, submissionid } = useParams();
  const qs = queryString.parse(location.search);
  console.log("Params", clientid, formid, submissionid);
  console.log("QS:", qs);

  //Get logged in user info
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  //The complete Form Submission object with form data
  const [formsyDetails, setFormsyDetails] = useState({});
  const [formsyConfig, setFormsyConfig] = useState({});
  const [loadingConfig, setLoadingConfig] = useState(false);
  //The form data and previous forms
  const [data, setData] = useState({});
  const [allFormSubmissions, setAllFormSubmissions] = useState([]);
  //Touched fields
  const [touched, setTouched] = useState({});
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  //Errors
  const [errors, setErrors] = useState({});
  //Client, Program and CareTeam Info
  const [clientData, setClientData] = useState({});
  const [careTeams, setCareTeams] = useState([]);

  //Signatures State Property
  const [signatures, setSignatures] = useState([]);
  //Show Signature Modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  //Comments state Property
  const [comments, setComments] = useState();
  //Show Comments Modal
  const [showComment, setShowComment] = useState(false);
  //Show Share Modal
  const [showShare, setShowShare] = useState(false);
  //Show Form Level (not field specific) Comments
  const [showFormComment, setShowFormComment] = useState(false);

  const [doCompare, setDoCompare] = useState(false);
  const [timeoutId, setTimeoutId] = useState();

  const pdfRef = useRef(true);
  const isMountedRef = useRef(true);

  //Get validation schema
  const schema = getSchema(formid);
  console.log("Schema Config: ", schema);

  const selectedProgramId = useMemo(() => {
    return data.SelectedProgram?.["@value"];
  }, [data.SelectedProgram]);

  const formDisabled = useMemo(() => {
    return formsyDetails.Active === "0" || formsyDetails.FinalSigned === "1";
  }, [formsyDetails]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      pdfRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (
      !initialProgram?.ccx_client_programid ||
      !data.SelectedProgram ||
      data.SelectedProgram["@value"]
    ) {
      return;
    }

    const obj = { ...data };
    obj.SelectedProgram["@value"] = initialProgram.ccx_client_programid;
    setData(obj);

    if (formsyDetails.ClientProgramId === initialProgram.ccx_client_programid) {
      return;
    }

    const details = { ...formsyDetails };
    details.ClientProgramId = initialProgram.ccx_client_programid;
    details.ClientProgramName = getFieldName(initialProgram.ccx_programunit);
    setFormsyDetails(details);
  }, [initialProgram, selectedProgramId, data, formsyDetails]);

  //Get Client Info
  useEffect(() => {
    if (!formid || !clientid) {
      return;
    }

    const loadClientInfo = async () => {
      const response = await Promise.all([
        getFormsySubmissionsForClient(formid, clientid),
        getCareTeams(clientid),
      ]);

      if (!isMountedRef.current || !response) {
        return;
      }

      setAllFormSubmissions(response[0]);
      setCareTeams(response[1]);
    };

    loadClientInfo();

    if (submissionid !== undefined) {
      const loadSubmission = async () => {
        const response = await getSubmission(submissionid);

        if (!isMountedRef.current || !response) {
          return;
        }

        setData(response.Data);
        setFormsyDetails(response.FormsyDetails);

        if (response.Comments) {
          setComments(response.Comments);
        }

        if (response.Signatures) {
          setSignatures(response.Signatures);
        }
      };

      loadSubmission();
    }
  }, [formid, clientid, submissionid]);

  useEffect(() => {
    if (
      !formid ||
      !clientid ||
      Object.keys(formsyConfig).length > 0 ||
      loadingConfig ||
      !safyInfo?.user
    ) {
      return;
    }

    const loadConfig = async () => {
      setLoadingConfig(true);
      const response = await getConfig(formid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setFormsyConfig(response);
      const clientDataResponse = await getClientData(
        clientid,
        submissionid !== undefined,
        `${response.FormName}${
          response.VersionIntId > 1 ? `v${response.VersionIntId}` : ""
        }`
      );

      if (!isMountedRef.current || !clientDataResponse) {
        return;
      }

      setClientData(clientDataResponse);

      if (!submissionid) {
        //Set Formsy Details
        const newForm = {
          SubmissionId: "",
          FormId: response.FormId,
          FormVersionId: formid,
          ContactId: clientid,
          ContactName: clientDataResponse.client.fullName,
          ClientProgramId: "",
          ClientProgramName: "",
          TertiaryObjectId: "",
          TertiaryObjectName: "",
          FormResults: {
            results: {},
          },

          Comments: "",
          Signatures: "",
          CreatedOn: "",
          CreatedById: safyInfo.user.azureId,
          CreatedByName: safyInfo.user.displayName,
          ModifiedById: safyInfo.user.azureId,
          ModifiedByName: safyInfo.user.displayName,
          FinalSigned: "0",
          FinalSignedDate: "",
          Active: "1",
        };
        setFormsyDetails(newForm);
        setData(response.FormConfiguration[0].config[0]);
        setLoadingConfig(false);

        console.log("New Form:", newForm);
        console.log(
          "loadConfig setData:",
          response.FormConfiguration[0].config[0]
        );
      }
    };

    loadConfig();
  }, [
    loadingConfig,
    formid,
    clientid,
    safyInfo?.user,
    formsyConfig,
    submissionid,
  ]);

  useEffect(() => {
    if (!submissionid || !!formsyDetails.SubmissionId) {
      return;
    }

    const obj = { ...formsyDetails };
    obj.SubmissionId = submissionid;
    setFormsyDetails(obj);
  }, [submissionid, formsyDetails]);

  const helperConfiguration = useMemo(() => {
    try {
      return JSON.parse(formsyConfig.HelperConfiguration[0]);
    } catch {
      const defaultConfig = {
        buttonBarConfig: {
          sign: {
            display: false,
            initialSignatureType: "full",
          },
          clone: {
            display: false,
          },
          delete: {
            display: false,
          },
          share: {
            display: false,
          },
          comment: {
            display: false,
          },
        },
        signatureType: "full",
        finalSignType: "Provider",
      };

      return defaultConfig;
    }
  }, [formsyConfig.HelperConfiguration]);

  const getSelectedProgramName = (dataObj, cData) => {
    if (!dataObj.SelectedProgram || !cData.programs) {
      return "";
    }

    let name = getFieldName(dataObj.SelectedProgram);
    if (name) {
      return name;
    } else {
      const selectedProgram = cData.programs.filter(
        (p) => p.ccx_client_programid === getFieldValue(dataObj.SelectedProgram)
      )[0];

      if (selectedProgram) {
        name = getFieldName(selectedProgram.ccx_programunit);
        const programDates = ` (${getFieldDate(
          selectedProgram.ccx_admitdate
        )} - ${
          selectedProgram.ccx_dischargedate
            ? getFieldDate(selectedProgram.ccx_dischargedate)
            : "no date"
        })`;
        return name + programDates;
      } else {
        return "";
      }
    }
  };

  //Form validation
  const validate = () => {
    const results = validateForm(data, schema);
    setErrors(results);
    return results;
  };

  const handleChange = ({ currentTarget: input }) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const result = handleFormChange(input, data, schema, errors);
    setData(result.Data);
    setErrors(result.Errors);

    if (!submissionid) {
      return result.Data;
    }

    const touchedObj = { ...touched, [input.name]: true };
    const touchedFields = Object.keys(touchedObj).filter(
      (field) => touchedObj[field]
    );

    if (touchedFields.length >= 5) {
      if (isSaveInProgress) {
        const toId = setTimeout(() => {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }, 20000);

        setTimeoutId(toId);
      } else {
        clearTimeout(timeoutId);
        handleSubmit(result.Data, null, null, null, true);
        touchedFields.forEach((field) => (touchedObj[field] = false));
      }
    } else {
      const toId = setTimeout(() => {
        if (isSaveInProgress) {
          const toId2 = setTimeout(() => {
            touchedFields.forEach((field) => (touchedObj[field] = false));
            handleSubmit(result.Data, null, null, null, true);
          }, 20000);

          setTimeoutId(toId2);
        } else {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }
      }, 20000);

      setTimeoutId(toId);
    }

    setTouched(touchedObj);
    return result.Data;
  };

  ////SAVE
  const handleSubmit = async (
    dataOverride,
    formsyDetailsOverride,
    commentsOverride,
    signaturesOverride,
    autoSave = false
  ) => {
    if (isSaveInProgress) {
      return;
    }

    const dataObj = dataOverride || data;
    const formsyDetailsObj = formsyDetailsOverride || formsyDetails;
    const commentsObj =
      commentsOverride === undefined ? undefined : commentsOverride || comments;
    const signaturesObj = signaturesOverride || signatures;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    setTouched({});
    const errs = { ...errors };

    if (!dataObj.SelectedProgram?.["@value"]) {
      errs.SelectedProgram = '"Selected Program" is required';
      setErrors(errs);
      return;
    }

    delete errs.SelectedProgram;
    setErrors(errs);
    setIsSaveInProgress(true);

    try {
      const submissionResponse = await saveForm(
        dataObj,
        formsyDetailsObj,
        commentsObj,
        signaturesObj,
        navigate,
        location,
        autoSave
      );

      if (!isMountedRef.current || !submissionResponse?.Id) {
        return;
      }

      const newDetails = { ...formsyDetailsObj };
      newDetails.ModifiedOn = submissionResponse.LastModifiedOn;
      newDetails.ModifiedById = safyInfo.user.azureId;
      newDetails.ModifiedByName = safyInfo.user.displayName;

      if (newDetails.SubmissionId === "") {
        newDetails.SubmissionId = submissionResponse?.Id;
      }

      setFormsyDetails(newDetails);
    } finally {
      setIsSaveInProgress(false);
    }
  };

  ////CLONE
  const handleOnClone = (event) => {
    event.preventDefault();
    cloneForm(
      data,
      formsyDetails,
      comments,
      signatures,
      navigate,
      location,
      formsyConfig
    );
    setSignatures([]);
  };

  ////DELETE
  const handleDelete = (e) => {
    e.preventDefault();
    if (submissionid) {
      deleteFormSubmission(
        data,
        formsyDetails,
        comments,
        signatures,
        navigate,
        location
      );
    } else {
      alert("The form could not be deleted because it has not been saved");
    }
  };

  ////SHARE
  const toggleShareModal = () => {
    setShowShare(!showShare);
  };

  const handleShareSend = (recipientList, shareComments) => {
    sendShareEmail(
      recipientList,
      shareComments,
      location.pathname,
      data,
      formsyDetails,
      comments,
      signatures,
      formsyConfig.FormDescription
    );
  };

  ////COMMENTS FUNCTIONALITY
  const [commentToEdit, setCommentToEdit] = useState();

  const toggleCommentModal = (editComment) => {
    if (!showComment && editComment) setCommentToEdit(editComment);
    setShowComment(!showComment);
  };

  const handleComments = ({ action, fieldName, comment, acknowledge }) => {
    if (action === "ADD") {
      setShowComment(true);
    } else if (comment !== undefined) {
      if (action === "EDIT") {
        toggleCommentModal(comment);
      } else if (action === "DELETE") {
        handleDeleteComment(comment);
      } else if (action === "ACKNOWLEDGE" && acknowledge !== undefined) {
        handleAcknowledgeComment(comment, acknowledge);
      }
    } else if (action === "GET") {
      return parsedComments.filter(
        (comment) => comment.field._text === fieldName
      );
    }
  };

  const commentFields = () => {
    let commentOptions = [];
    commentOptions.push({ _id: "EntireForm", name: "Entire Form----" });
    for (const property in data) {
      if (
        data[property]["@type"] !== "section" &&
        data[property]["@type"] !== "label" &&
        data[property]["@type"] !== "User" &&
        data[property]["@type"] !== "hidden"
      ) {
        const option = {
          _id: property,
          name:
            data[property]["@label"] !== undefined &&
            data[property]["@label"] !== ""
              ? data[property]["@label"]
              : property,
        };
        commentOptions.push(option);
      }
    }
    return commentOptions;
  };

  const parsedComments = useMemo(() => {
    if (!comments) {
      return [];
    }

    const commentObj = xml2js(`<comments type="array">${comments}</comments>`, {
      compact: true,
      spaces: 4,
    });

    return ArrayCheck(commentObj.comments.comment);
  }, [comments]);

  const onCommentSave = (comment, commentToEdit) => {
    let formsyComments;
    if (commentToEdit) {
      const commentXml = `<comment><createdby>${
        commentToEdit.createdby._text
      }</createdby><datetime>${commentToEdit.datetime._text}</datetime><field>${
        commentToEdit.field._text
      }</field><comment>${commentToEdit.comment._text}</comment>${
        commentToEdit.acknowledgedby
          ? `<acknowledgedby>${commentToEdit.acknowledgedby._text}</acknowledgedby><acknowledgedon>${commentToEdit.acknowledgedon._text}</acknowledgedon>`
          : ""
      }</comment>`;
      console.log("CurrentCommentsXML: ", comments);
      console.log("CommentToReplace: ", commentXml);
      console.log("CommentToEdit: ", commentToEdit);
      console.log("CommentToEditXML: ", comment);

      formsyComments =
        comments !== undefined
          ? comments.replace(commentXml, comment)
          : comment;
      setComments(formsyComments);
    } else {
      formsyComments = comments !== undefined ? comments + comment : comment;
      setComments(formsyComments);
    }

    setShowComment(false);
    handleSubmit(null, null, formsyComments, null);
    setCommentToEdit(undefined);
  };

  const handleDeleteComment = (comment) => {
    const commentXml = `<comment><createdby>${
      safyInfo.user.displayName
    }</createdby><datetime>${comment.datetime._text}</datetime><field>${
      comment.field._text
    }</field><comment>${comment.comment._text}</comment>${
      comment.acknowledgedby
        ? `<acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon>`
        : ""
    }</comment>`;

    let formsyComments = comments.replace(commentXml, "");
    if (formsyComments === "") formsyComments = undefined;
    setComments(formsyComments);
    handleSubmit(null, null, formsyComments, null);
  };

  const toggleCommentToEdit = (comment) => {
    setCommentToEdit(comment);
    setShowComment(true);
  };

  const handleAcknowledgeComment = (comment, acknowledge) => {
    if (acknowledge) {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const newCommentXML = `<comment><createdby>${
        comment.createdby._text
      }</createdby><datetime>${comment.datetime._text}</datetime><field>${
        comment.field._text
      }</field><comment>${comment.comment._text}</comment><acknowledgedby>${
        safyInfo.user.displayName
      }</acknowledgedby><acknowledgedon>${moment().format(
        "L"
      )} ${moment().format("LT")}</acknowledgedon></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    } else {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment><acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon></comment>`;
      const newCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    }
  };

  const handleCommentNavigation = (fieldName) => {
    const element = document.getElementById(fieldName);
    if (element.type === "checkbox")
      scrollTo(
        document.getElementById(fieldName).parentElement.parentElement
          .parentElement.offsetTop - 50,
        () => {
          document.getElementById(fieldName).focus();
        }
      );
    else
      scrollTo(document.getElementById(fieldName).offsetTop - 50, () => {
        document.getElementById(fieldName).focus();
      });
  };

  const renderComments = () => {
    return parsedComments
      .filter(
        (comment) =>
          comment.field._text === "EntireForm" ||
          comment.field._text === "Entire Form----"
      )
      .map((comment, index) => (
        <div className="mt-2" key={`form-comment-${index}`}>
          {showFormComment && (
            <div
              className={`${
                comment.acknowledgedby ? "bg-safy3-50" : "bg-warning"
              } p-2 m-2 rounded d-print-none`}
              title={
                comment.acknowledgedby
                  ? `Acknowledged by: ${comment.acknowledgedby._text} ${comment.acknowledgedon._text}`
                  : ""
              }
            >
              <span className="h6">
                {comment.createdby._text ===
                  JSON.parse(localStorage.getItem("safyInfo")).user
                    .displayName && (
                  <>
                    <i
                      title="Delete Comment"
                      className="far fa-trash-alt text-white me-2"
                      onClick={() => {
                        confirmAlert({
                          title: "Delete Comment",
                          message:
                            "Are you sure you want to delete this comment?",
                          buttons: [
                            {
                              label: "Yes",
                              onClick: () => {
                                handleDeleteComment(comment);
                              },
                            },
                            {
                              label: "No",
                              onClick: () => {
                                /* do nothing */
                              },
                            },
                          ],
                        });
                      }}
                    ></i>
                    {!comment.acknowledgedby && (
                      <i
                        title="Edit Comment"
                        className="fas fa-pen text-white me-2"
                        onClick={(e) => {
                          toggleCommentToEdit(comment);
                        }}
                      ></i>
                    )}
                  </>
                )}
                {comment.acknowledgedby && (
                  <i
                    className="far fa-check-square text-white me-2"
                    onClick={() => {
                      if (
                        comment.acknowledgedby._text ===
                        JSON.parse(localStorage.getItem("safyInfo")).user
                          .displayName
                      )
                        handleAcknowledgeComment(comment, false);
                    }}
                  ></i>
                )}
                {!comment.acknowledgedby && (
                  <i
                    className="far fa-square text-white me-2"
                    onClick={() => {
                      handleAcknowledgeComment(comment, true);
                    }}
                    title="Acknowledge Comment"
                  ></i>
                )}
                <br />
                {`${comment.createdby._text} - ${comment.datetime._text}`}
              </span>
              <br />
              Regarding:{" "}
              {comment.field._text !== "EntireForm" &&
              comment.field._text !== "Entire Form----" ? (
                <button
                  className="btn btn-link"
                  onClick={() => {
                    setTimeout(() => {
                      handleCommentNavigation(comment.field._text);
                    }, 100);
                  }}
                >
                  {comment.field._text}
                </button>
              ) : (
                <>{comment.field._text}</>
              )}
              <br />
              {comment.comment._text}
            </div>
          )}
        </div>
      ));
  };

  function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener("scroll", onScroll);
        callback();
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  //SIGNATURE FUNCTIONALITY
  const toggleSignatureModal = (e) => {
    e.preventDefault();
    if (!isSaveInProgress) setShowSignatureModal(true);
  };

  const saveSignature = async (signatureData) => {
    console.log("Signature Data:", signatureData);

    let sigArray = [];
    if (Array.isArray(signatureData)) {
      sigArray = signatureData;
    } else {
      sigArray.push(signatureData);
    }
    setSignatures(sigArray);

    const formsyDetailsObj = { ...formsyDetails };

    if (formsyDetailsObj.FinalSigned !== "1" && checkFinalSigned(sigArray)) {
      console.log("Final Signed");
      formsyDetailsObj.FinalSigned = "1";

      if (
        formsyDetailsObj.FinalSignedDate === "" ||
        formsyDetailsObj.FinalSignedDate === "1900-01-01T00:00:00"
      ) {
        formsyDetailsObj.FinalSignedDate = new Date();
      }

      setFormsyDetails(formsyDetailsObj);
      setComments(undefined);
    }

    handleSubmit(
      null,
      formsyDetailsObj,
      formsyDetailsObj.FinalSigned === "1" ? undefined : null,
      sigArray
    );
  };

  const checkFinalSigned = (sigArray) => {
    const requiredSignatureTypes = helperConfiguration.finalSignType.split(",");

    const capturedSignatureTypes = sigArray.map((sig) => sig.signatureType);
    console.log("requiredSignatureTypes: ", requiredSignatureTypes);
    console.log("capturedSignatureTypes: ", capturedSignatureTypes);

    const missingSigTypes = requiredSignatureTypes.filter(
      (type) => !capturedSignatureTypes.includes(type)
    );

    return missingSigTypes.length === 0;
  };

  //COMPARE
  const compare = (field) => {
    if (doCompare)
      return compareForms(field, data, allFormSubmissions, formsyDetails);
  };

  const toggleCompare = () => {
    setDoCompare(!doCompare);
  };

  //CUSTOM

  ////UI
  return (
    <>
      <div className="mb-2">
        <BackIcon />
      </div>
      <div className={!submissionid ? "watermarked" : ""} ref={pdfRef}>
        <ClientFormHeader
          formTitle={formsyConfig.FormDescription}
          clientData={clientData.client}
          formsyDetails={formsyDetails}
        ></ClientFormHeader>
        <FormButtonBar
          config={
            formsyConfig.HelperConfiguration
              ? JSON.parse(formsyConfig.HelperConfiguration[0]).buttonBarConfig
              : {
                  sign: {
                    display: false,
                    initialSignatureType: "supervisor",
                  },
                  clone: {
                    display: false,
                  },
                  delete: {
                    display: false,
                  },
                  share: {
                    display: false,
                  },
                  comment: {
                    display: false,
                  },
                }
          }
          onSave={handleSubmit}
          onClone={handleOnClone}
          onDelete={handleDelete}
          onSignClicked={toggleSignatureModal}
          onComment={toggleCommentModal}
          onCompare={toggleCompare}
          onShare={toggleShareModal}
          signed={signatures.length > 0}
          finalSigned={formsyDetails.FinalSigned === "1"}
          validate={validate}
          pdfRef={pdfRef}
          filename={`${formsyConfig.FormDescription}`}
          newestVersion={
            formsyConfig.VersionIntId === formsyConfig.LatestVersionIntId
          }
          printStyle={`.container {
            min-width: 100% !important;
        }
    
        .py-2 {
            padding: 0 !important;
            font-size: 10pt;
        }`}
        />
        <div className="py-2 border-bottom border-dark">
          <b>Selected Client Program:</b>{" "}
          {getSelectedProgramName(data, clientData)}
        </div>
        {renderComments()}
        {parsedComments.filter(
          (comment) =>
            comment.field._text === "EntireForm" ||
            comment.field._text === "Entire Form----"
        ).length > 0 && (
          <i
            className="fas fa-comment-alt text-warning float-end mb-2 ms-2"
            onClick={() => setShowFormComment(!showFormComment)}
          ></i>
        )}
        <div className="mt-4">
          {data && Object.keys(data).length > 0 && (
            <>
              <div className="row">
                <div className="col">
                  <FormInput
                    name="Parent"
                    label="Parent Partner:"
                    error={errors.Parent}
                    value={data.Parent["@value"]}
                    onChange={handleChange}
                    identical={compare("Parent")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  <FormInput
                    name="Date"
                    label="Date:"
                    error={errors.Date}
                    value={data.Date["@value"]}
                    onChange={handleChange}
                    identical={compare("Date")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <br />
              <div className="container border">
                <div className="row border-bottom">
                  <div className="col py-2">
                    Using the scale below, mark the appropriate box to indicate
                    your response for each item.
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-5 border-end"></div>
                  <div className="col py-2 text-center border-end">Never</div>
                  <div className="col py-2 text-center border-end">Rarely</div>
                  <div className="col py-2 text-center border-end">
                    Sometimes
                  </div>
                  <div className="col py-2 text-center border-end">Often</div>
                  <div className="col py-2 text-center border-end">Always</div>
                  <div className="col py-2 text-center">
                    My parent
                    <br />
                    declined
                    <br />
                    or did not
                    <br />
                    participate
                    <br />
                    (N/A)
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col py-2">The Parent Partner...</div>
                </div>
                <div className="row border-bottom">
                  <div className="col-5 py-2 border-end">
                    <div className="row">
                      <div className="col-1 text-end pe-1">1.</div>
                      <div className="col ps-1">
                        Encouraged the participant to fulfill their case plan
                        activities
                      </div>
                    </div>
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="Fulfill"
                      error={errors.Fulfill}
                      value={data.Fulfill["@value"]}
                      options={[{ _id: "Never", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="Fulfill"
                      error={errors.Fulfill}
                      value={data.Fulfill["@value"]}
                      options={[{ _id: "Rarely", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="Fulfill"
                      error={errors.Fulfill}
                      value={data.Fulfill["@value"]}
                      options={[{ _id: "Sometimes", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="Fulfill"
                      error={errors.Fulfill}
                      value={data.Fulfill["@value"]}
                      options={[{ _id: "Often", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="Fulfill"
                      error={errors.Fulfill}
                      value={data.Fulfill["@value"]}
                      options={[{ _id: "Always", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2">
                    <FormRadioButton
                      name="Fulfill"
                      error={errors.Fulfill}
                      value={data.Fulfill["@value"]}
                      options={[{ _id: "N/A", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-5 py-2 border-end">
                    <div className="row">
                      <div className="col-1 text-end pe-1">2.</div>
                      <div className="col ps-1">
                        Had regular face to face visits with the participant
                      </div>
                    </div>
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="Visits"
                      error={errors.Visits}
                      value={data.Visits["@value"]}
                      options={[{ _id: "Never", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="Visits"
                      error={errors.Visits}
                      value={data.Visits["@value"]}
                      options={[{ _id: "Rarely", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="Visits"
                      error={errors.Visits}
                      value={data.Visits["@value"]}
                      options={[{ _id: "Sometimes", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="Visits"
                      error={errors.Visits}
                      value={data.Visits["@value"]}
                      options={[{ _id: "Often", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="Visits"
                      error={errors.Visits}
                      value={data.Visits["@value"]}
                      options={[{ _id: "Always", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2">
                    <FormRadioButton
                      name="Visits"
                      error={errors.Visits}
                      value={data.Visits["@value"]}
                      options={[{ _id: "N/A", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-5 py-2 border-end">
                    <div className="row">
                      <div className="col-1 text-end pe-1">3.</div>
                      <div className="col ps-1">
                        Had other (email, phone, web) communication and contact
                        with the participant
                      </div>
                    </div>
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="OtherContact"
                      error={errors.OtherContact}
                      value={data.OtherContact["@value"]}
                      options={[{ _id: "Never", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="OtherContact"
                      error={errors.OtherContact}
                      value={data.OtherContact["@value"]}
                      options={[{ _id: "Rarely", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="OtherContact"
                      error={errors.OtherContact}
                      value={data.OtherContact["@value"]}
                      options={[{ _id: "Sometimes", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="OtherContact"
                      error={errors.OtherContact}
                      value={data.OtherContact["@value"]}
                      options={[{ _id: "Often", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="OtherContact"
                      error={errors.OtherContact}
                      value={data.OtherContact["@value"]}
                      options={[{ _id: "Always", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2">
                    <FormRadioButton
                      name="OtherContact"
                      error={errors.OtherContact}
                      value={data.OtherContact["@value"]}
                      options={[{ _id: "N/A", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-5 py-2 border-end">
                    <div className="row">
                      <div className="col-1 text-end pe-1">4.</div>
                      <div className="col ps-1">
                        Advocated for the participant for needed resources
                      </div>
                    </div>
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="Advocated"
                      error={errors.Advocated}
                      value={data.Advocated["@value"]}
                      options={[{ _id: "Never", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="Advocated"
                      error={errors.Advocated}
                      value={data.Advocated["@value"]}
                      options={[{ _id: "Rarely", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="Advocated"
                      error={errors.Advocated}
                      value={data.Advocated["@value"]}
                      options={[{ _id: "Sometimes", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="Advocated"
                      error={errors.Advocated}
                      value={data.Advocated["@value"]}
                      options={[{ _id: "Often", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="Advocated"
                      error={errors.Advocated}
                      value={data.Advocated["@value"]}
                      options={[{ _id: "Always", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2">
                    <FormRadioButton
                      name="Advocated"
                      error={errors.Advocated}
                      value={data.Advocated["@value"]}
                      options={[{ _id: "N/A", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-5 py-2 border-end">
                    <div className="row">
                      <div className="col-1 text-end pe-1">5.</div>
                      <div className="col ps-1">Encouraged the participant</div>
                    </div>
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="Encouraged"
                      error={errors.Encouraged}
                      value={data.Encouraged["@value"]}
                      options={[{ _id: "Never", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="Encouraged"
                      error={errors.Encouraged}
                      value={data.Encouraged["@value"]}
                      options={[{ _id: "Rarely", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="Encouraged"
                      error={errors.Encouraged}
                      value={data.Encouraged["@value"]}
                      options={[{ _id: "Sometimes", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="Encouraged"
                      error={errors.Encouraged}
                      value={data.Encouraged["@value"]}
                      options={[{ _id: "Often", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="Encouraged"
                      error={errors.Encouraged}
                      value={data.Encouraged["@value"]}
                      options={[{ _id: "Always", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2">
                    <FormRadioButton
                      name="Encouraged"
                      error={errors.Encouraged}
                      value={data.Encouraged["@value"]}
                      options={[{ _id: "N/A", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-5 py-2 border-end">
                    <div className="row">
                      <div className="col-1 text-end pe-1">6.</div>
                      <div className="col ps-1">
                        Connected the participant with community resources
                      </div>
                    </div>
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="Connected"
                      error={errors.Connected}
                      value={data.Connected["@value"]}
                      options={[{ _id: "Never", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="Connected"
                      error={errors.Connected}
                      value={data.Connected["@value"]}
                      options={[{ _id: "Rarely", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="Connected"
                      error={errors.Connected}
                      value={data.Connected["@value"]}
                      options={[{ _id: "Sometimes", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="Connected"
                      error={errors.Connected}
                      value={data.Connected["@value"]}
                      options={[{ _id: "Often", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="Connected"
                      error={errors.Connected}
                      value={data.Connected["@value"]}
                      options={[{ _id: "Always", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2">
                    <FormRadioButton
                      name="Connected"
                      error={errors.Connected}
                      value={data.Connected["@value"]}
                      options={[{ _id: "N/A", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-5 py-2 border-end">
                    <div className="row">
                      <div className="col-1 text-end pe-1">7.</div>
                      <div className="col ps-1">
                        Helped the participant connect with the community
                      </div>
                    </div>
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="HelpConnect"
                      error={errors.HelpConnect}
                      value={data.HelpConnect["@value"]}
                      options={[{ _id: "Never", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="HelpConnect"
                      error={errors.HelpConnect}
                      value={data.HelpConnect["@value"]}
                      options={[{ _id: "Rarely", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="HelpConnect"
                      error={errors.HelpConnect}
                      value={data.HelpConnect["@value"]}
                      options={[{ _id: "Sometimes", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="HelpConnect"
                      error={errors.HelpConnect}
                      value={data.HelpConnect["@value"]}
                      options={[{ _id: "Often", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="HelpConnect"
                      error={errors.HelpConnect}
                      value={data.HelpConnect["@value"]}
                      options={[{ _id: "Always", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2">
                    <FormRadioButton
                      name="HelpConnect"
                      error={errors.HelpConnect}
                      value={data.HelpConnect["@value"]}
                      options={[{ _id: "N/A", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-5 py-2 border-end">
                    <div className="row">
                      <div className="col-1 text-end pe-1">8.</div>
                      <div className="col ps-1">
                        Coached the participant on communication strategies
                      </div>
                    </div>
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="Communication"
                      error={errors.Communication}
                      value={data.Communication["@value"]}
                      options={[{ _id: "Never", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="Communication"
                      error={errors.Communication}
                      value={data.Communication["@value"]}
                      options={[{ _id: "Rarely", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="Communication"
                      error={errors.Communication}
                      value={data.Communication["@value"]}
                      options={[{ _id: "Sometimes", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="Communication"
                      error={errors.Communication}
                      value={data.Communication["@value"]}
                      options={[{ _id: "Often", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="Communication"
                      error={errors.Communication}
                      value={data.Communication["@value"]}
                      options={[{ _id: "Always", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2">
                    <FormRadioButton
                      name="Communication"
                      error={errors.Communication}
                      value={data.Communication["@value"]}
                      options={[{ _id: "N/A", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-5 py-2 border-end">
                    <div className="row">
                      <div className="col-1 text-end pe-1">9.</div>
                      <div className="col ps-1">
                        Supported the participant at FTDM, court, treatment,
                        other gatherings
                      </div>
                    </div>
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="Supported"
                      error={errors.Supported}
                      value={data.Supported["@value"]}
                      options={[{ _id: "Never", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="Supported"
                      error={errors.Supported}
                      value={data.Supported["@value"]}
                      options={[{ _id: "Rarely", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="Supported"
                      error={errors.Supported}
                      value={data.Supported["@value"]}
                      options={[{ _id: "Sometimes", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="Supported"
                      error={errors.Supported}
                      value={data.Supported["@value"]}
                      options={[{ _id: "Often", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="Supported"
                      error={errors.Supported}
                      value={data.Supported["@value"]}
                      options={[{ _id: "Always", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2">
                    <FormRadioButton
                      name="Supported"
                      error={errors.Supported}
                      value={data.Supported["@value"]}
                      options={[{ _id: "N/A", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-5 py-2 border-end">
                    <div className="row">
                      <div className="col-1 text-end pe-1">10.</div>
                      <div className="col ps-1">
                        Coached the participant on what to expect throughout the
                        process
                      </div>
                    </div>
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="Coached"
                      error={errors.Coached}
                      value={data.Coached["@value"]}
                      options={[{ _id: "Never", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="Coached"
                      error={errors.Coached}
                      value={data.Coached["@value"]}
                      options={[{ _id: "Rarely", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="Coached"
                      error={errors.Coached}
                      value={data.Coached["@value"]}
                      options={[{ _id: "Sometimes", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="Coached"
                      error={errors.Coached}
                      value={data.Coached["@value"]}
                      options={[{ _id: "Often", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="Coached"
                      error={errors.Coached}
                      value={data.Coached["@value"]}
                      options={[{ _id: "Always", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2">
                    <FormRadioButton
                      name="Coached"
                      error={errors.Coached}
                      value={data.Coached["@value"]}
                      options={[{ _id: "N/A", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col py-2">
                    Please provide additional comments regarding the performance
                    on these activities throughout the Parent Partner Program.
                    If you rated any of the above statements as "Always" or
                    "Rarely" or "Never" please explain why below.
                  </div>
                </div>
                <div className="row">
                  <div className="col py-2">
                    <FormTextArea
                      name="Comments"
                      error={errors.Comments}
                      value={data.Comments["@value"]}
                      onChange={handleChange}
                      identical={compare("Comments")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
              </div>
              <br />
              <div className="container border">
                <div className="row border-bottom">
                  <div className="col py-2">
                    Using the scale below, mark the appropriate box to indicate
                    your response for each item.
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-5 border-end"></div>
                  <div className="col py-2 text-center border-end">
                    Significant Improvement
                  </div>
                  <div className="col py-2 text-center border-end">
                    Some Improvement
                  </div>
                  <div className="col py-2 text-center border-end">
                    Remained
                    <br />
                    the Same
                  </div>
                  <div className="col py-2 text-center border-end">
                    Decreased
                  </div>
                  <div className="col py-2 text-center border-end">
                    Don't know
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col py-2">
                    Please rate the participant improvement on their...
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-5 py-2 border-end">
                    <div className="row">
                      <div className="col-1 text-end pe-1">1.</div>
                      <div className="col ps-1">
                        Relationship with people who are able to connect them
                        with resources
                      </div>
                    </div>
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="ConnectRelationship"
                      error={errors.ConnectRelationship}
                      value={data.ConnectRelationship["@value"]}
                      options={[{ _id: "Significant Improvement", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="ConnectRelationship"
                      error={errors.ConnectRelationship}
                      value={data.ConnectRelationship["@value"]}
                      options={[{ _id: "Some Improvement", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="ConnectRelationship"
                      error={errors.ConnectRelationship}
                      value={data.ConnectRelationship["@value"]}
                      options={[{ _id: "Remained the Same", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="ConnectRelationship"
                      error={errors.ConnectRelationship}
                      value={data.ConnectRelationship["@value"]}
                      options={[{ _id: "Decreased", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2">
                    <FormRadioButton
                      name="ConnectRelationship"
                      error={errors.ConnectRelationship}
                      value={data.ConnectRelationship["@value"]}
                      options={[{ _id: "Don't Know", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-5 py-2 border-end">
                    <div className="row">
                      <div className="col-1 text-end pe-1">2.</div>
                      <div className="col ps-1">
                        Relationship with people who support their positive
                        changes
                      </div>
                    </div>
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="SupportRelationship"
                      error={errors.SupportRelationship}
                      value={data.SupportRelationship["@value"]}
                      options={[{ _id: "Significant Improvement", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="SupportRelationship"
                      error={errors.SupportRelationship}
                      value={data.SupportRelationship["@value"]}
                      options={[{ _id: "Some Improvement", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="SupportRelationship"
                      error={errors.SupportRelationship}
                      value={data.SupportRelationship["@value"]}
                      options={[{ _id: "Remained the Same", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="SupportRelationship"
                      error={errors.SupportRelationship}
                      value={data.SupportRelationship["@value"]}
                      options={[{ _id: "Decreased", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2">
                    <FormRadioButton
                      name="SupportRelationship"
                      error={errors.SupportRelationship}
                      value={data.SupportRelationship["@value"]}
                      options={[{ _id: "Don't Know", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-5 py-2 border-end">
                    <div className="row">
                      <div className="col-1 text-end pe-1">3.</div>
                      <div className="col ps-1">
                        Level of communication with their DHS worker
                      </div>
                    </div>
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="DHSCommunication"
                      error={errors.DHSCommunication}
                      value={data.DHSCommunication["@value"]}
                      options={[{ _id: "Significant Improvement", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="DHSCommunication"
                      error={errors.DHSCommunication}
                      value={data.DHSCommunication["@value"]}
                      options={[{ _id: "Some Improvement", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="DHSCommunication"
                      error={errors.DHSCommunication}
                      value={data.DHSCommunication["@value"]}
                      options={[{ _id: "Remained the Same", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="DHSCommunication"
                      error={errors.DHSCommunication}
                      value={data.DHSCommunication["@value"]}
                      options={[{ _id: "Decreased", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2">
                    <FormRadioButton
                      name="DHSCommunication"
                      error={errors.DHSCommunication}
                      value={data.DHSCommunication["@value"]}
                      options={[{ _id: "Don't Know", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-5 py-2 border-end">
                    <div className="row">
                      <div className="col-1 text-end pe-1">4.</div>
                      <div className="col ps-1">
                        Level of communication with attorney(s)
                      </div>
                    </div>
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="AttorneyCommunication"
                      error={errors.AttorneyCommunication}
                      value={data.AttorneyCommunication["@value"]}
                      options={[{ _id: "Significant Improvement", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="AttorneyCommunication"
                      error={errors.AttorneyCommunication}
                      value={data.AttorneyCommunication["@value"]}
                      options={[{ _id: "Some Improvement", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="AttorneyCommunication"
                      error={errors.AttorneyCommunication}
                      value={data.AttorneyCommunication["@value"]}
                      options={[{ _id: "Remained the Same", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="AttorneyCommunication"
                      error={errors.AttorneyCommunication}
                      value={data.AttorneyCommunication["@value"]}
                      options={[{ _id: "Decreased", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2">
                    <FormRadioButton
                      name="AttorneyCommunication"
                      error={errors.AttorneyCommunication}
                      value={data.AttorneyCommunication["@value"]}
                      options={[{ _id: "Don't Know", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-5 py-2 border-end">
                    <div className="row">
                      <div className="col-1 text-end pe-1">5.</div>
                      <div className="col ps-1">
                        Ability to advocate appropriately for themselves &amp;
                        family
                      </div>
                    </div>
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="Advocate"
                      error={errors.Advocate}
                      value={data.Advocate["@value"]}
                      options={[{ _id: "Significant Improvement", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="Advocate"
                      error={errors.Advocate}
                      value={data.Advocate["@value"]}
                      options={[{ _id: "Some Improvement", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="Advocate"
                      error={errors.Advocate}
                      value={data.Advocate["@value"]}
                      options={[{ _id: "Remained the Same", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="Advocate"
                      error={errors.Advocate}
                      value={data.Advocate["@value"]}
                      options={[{ _id: "Decreased", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2">
                    <FormRadioButton
                      name="Advocate"
                      error={errors.Advocate}
                      value={data.Advocate["@value"]}
                      options={[{ _id: "Don't Know", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-5 py-2 border-end">
                    <div className="row">
                      <div className="col-1 text-end pe-1">6.</div>
                      <div className="col ps-1">
                        Knowledge of what needs to be done for custody of their
                        children
                      </div>
                    </div>
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="Custody"
                      error={errors.Custody}
                      value={data.Custody["@value"]}
                      options={[{ _id: "Significant Improvement", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="Custody"
                      error={errors.Custody}
                      value={data.Custody["@value"]}
                      options={[{ _id: "Some Improvement", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="Custody"
                      error={errors.Custody}
                      value={data.Custody["@value"]}
                      options={[{ _id: "Remained the Same", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="Custody"
                      error={errors.Custody}
                      value={data.Custody["@value"]}
                      options={[{ _id: "Decreased", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2">
                    <FormRadioButton
                      name="Custody"
                      error={errors.Custody}
                      value={data.Custody["@value"]}
                      options={[{ _id: "Don't Know", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-5 py-2 border-end">
                    <div className="row">
                      <div className="col-1 text-end pe-1">7.</div>
                      <div className="col ps-1">
                        Ability to get to appointments on time (visitation,
                        FTMs, counseling session, substance abuse treatment,
                        etc.)
                      </div>
                    </div>
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="ReliableAppts"
                      error={errors.ReliableAppts}
                      value={data.ReliableAppts["@value"]}
                      options={[{ _id: "Significant Improvement", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="ReliableAppts"
                      error={errors.ReliableAppts}
                      value={data.ReliableAppts["@value"]}
                      options={[{ _id: "Some Improvement", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="ReliableAppts"
                      error={errors.ReliableAppts}
                      value={data.ReliableAppts["@value"]}
                      options={[{ _id: "Remained the Same", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="ReliableAppts"
                      error={errors.ReliableAppts}
                      value={data.ReliableAppts["@value"]}
                      options={[{ _id: "Decreased", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2">
                    <FormRadioButton
                      name="ReliableAppts"
                      error={errors.ReliableAppts}
                      value={data.ReliableAppts["@value"]}
                      options={[{ _id: "Don't Know", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-5 py-2 border-end">
                    <div className="row">
                      <div className="col-1 text-end pe-1">8.</div>
                      <div className="col ps-1">
                        Ability to find community resources for their family
                      </div>
                    </div>
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="FindResources"
                      error={errors.FindResources}
                      value={data.FindResources["@value"]}
                      options={[{ _id: "Significant Improvement", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="FindResources"
                      error={errors.FindResources}
                      value={data.FindResources["@value"]}
                      options={[{ _id: "Some Improvement", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="FindResources"
                      error={errors.FindResources}
                      value={data.FindResources["@value"]}
                      options={[{ _id: "Remained the Same", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="FindResources"
                      error={errors.FindResources}
                      value={data.FindResources["@value"]}
                      options={[{ _id: "Decreased", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2">
                    <FormRadioButton
                      name="FindResources"
                      error={errors.FindResources}
                      value={data.FindResources["@value"]}
                      options={[{ _id: "Don't Know", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-5 py-2 border-end">
                    <div className="row">
                      <div className="col-1 text-end pe-1">9.</div>
                      <div className="col ps-1">
                        Knowledge of who to contact with needs or concerns
                        regarding their case
                      </div>
                    </div>
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="NeedsContact"
                      error={errors.NeedsContact}
                      value={data.NeedsContact["@value"]}
                      options={[{ _id: "Significant Improvement", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="NeedsContact"
                      error={errors.NeedsContact}
                      value={data.NeedsContact["@value"]}
                      options={[{ _id: "Some Improvement", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="NeedsContact"
                      error={errors.NeedsContact}
                      value={data.NeedsContact["@value"]}
                      options={[{ _id: "Remained the Same", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="NeedsContact"
                      error={errors.NeedsContact}
                      value={data.NeedsContact["@value"]}
                      options={[{ _id: "Decreased", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2">
                    <FormRadioButton
                      name="NeedsContact"
                      error={errors.NeedsContact}
                      value={data.NeedsContact["@value"]}
                      options={[{ _id: "Don't Know", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-5 py-2 border-end">
                    <div className="row">
                      <div className="col-1 text-end pe-1">10.</div>
                      <div className="col ps-1">
                        Level of personal responsibility and accountability for
                        their actions
                      </div>
                    </div>
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="Responsibility"
                      error={errors.Responsibility}
                      value={data.Responsibility["@value"]}
                      options={[{ _id: "Significant Improvement", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="Responsibility"
                      error={errors.Responsibility}
                      value={data.Responsibility["@value"]}
                      options={[{ _id: "Some Improvement", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="Responsibility"
                      error={errors.Responsibility}
                      value={data.Responsibility["@value"]}
                      options={[{ _id: "Remained the Same", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="Responsibility"
                      error={errors.Responsibility}
                      value={data.Responsibility["@value"]}
                      options={[{ _id: "Decreased", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2">
                    <FormRadioButton
                      name="Responsibility"
                      error={errors.Responsibility}
                      value={data.Responsibility["@value"]}
                      options={[{ _id: "Don't Know", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col-5 py-2 border-end">
                    <div className="row">
                      <div className="col-1 text-end pe-1">11.</div>
                      <div className="col ps-1">
                        Willingness to make changes
                      </div>
                    </div>
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="Willingness"
                      error={errors.Willingness}
                      value={data.Willingness["@value"]}
                      options={[{ _id: "Significant Improvement", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="Willingness"
                      error={errors.Willingness}
                      value={data.Willingness["@value"]}
                      options={[{ _id: "Some Improvement", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="Willingness"
                      error={errors.Willingness}
                      value={data.Willingness["@value"]}
                      options={[{ _id: "Remained the Same", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2 border-end">
                    <FormRadioButton
                      name="Willingness"
                      error={errors.Willingness}
                      value={data.Willingness["@value"]}
                      options={[{ _id: "Decreased", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col py-2">
                    <FormRadioButton
                      name="Willingness"
                      error={errors.Willingness}
                      value={data.Willingness["@value"]}
                      options={[{ _id: "Don't Know", name: "" }]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row border-bottom">
                  <div className="col py-2">
                    If you rated any of the above statemens as "Significant
                    Improvement" or "Remaind the Same" or "Decreased," please
                    explain why below. Note that "Remained the Same" could mean
                    that no change was needed, or the indicator was satisfactory
                    to begin with.
                  </div>
                </div>
                <div className="row">
                  <div className="col py-2">
                    <FormTextArea
                      name="RatingComments"
                      error={errors.RatingComments}
                      value={data.RatingComments["@value"]}
                      onChange={handleChange}
                      identical={compare("RatingComments")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
              </div>
            </>
          )}
          <div>
            <div
              className="btn btn-primary m-2 d-print-none"
              onClick={() => {
                handleSubmit(null, null, null, null);
              }}
            >
              Save
            </div>
            <Signature
              canvasWidth="450"
              canvasHeight="200"
              modalTitle="Report Signatures"
              careTeams={careTeams}
              signatures={signatures}
              onSign={saveSignature}
              clientData={clientData.client}
              showModal={showSignatureModal}
              onHide={() => {
                setShowSignatureModal(false);
              }}
              finalSignType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).finalSignType
              }
              initialSignatureType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).signatureType
              }
            ></Signature>
            <Comment
              showModal={showComment}
              commentToEdit={commentToEdit}
              onHide={() => {
                setShowComment(false);
                setCommentToEdit(undefined);
              }}
              onSave={onCommentSave}
              fields={commentFields()}
            ></Comment>
            {showShare ? (
              <ShareModal
                showShare={showShare}
                onHide={() => setShowShare(false)}
                onSend={handleShareSend}
                submissionId={submissionid}
              ></ShareModal>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

export default SCPPOutcome;
